import { useState } from "react";
import Button from "components/common/controls/Button";
import FormGroup from "./FormGroup";
import InputField from "./controls/InputField";
import PromineoPhoneInput, {
  PhoneInputValueChangedEventArg,
} from "./controls/PromineoPhoneInput";
import { SignInSignUpOption } from "shared/enums/SignInSignUpOptionEnum";

export interface UserEmailOrPhoneIdentifier {
  type: SignInSignUpOption;
  email?: string;
  phone?: string;
  phoneCode?: string;
  phoneNumberWithCode?: string;
}

interface Props {
  defaultSelection?: SignInSignUpOption;
  showPhoneLabelTips?: boolean;
  label: string;
  email?: string;
  phoneNumberWithCode?: string;
  onOptionChanged?: (opt: SignInSignUpOption) => void;
  onValueChanged?: (arg: UserEmailOrPhoneIdentifier) => void;
  onPhoneChanged?: (arg: UserEmailOrPhoneIdentifier) => void;
  onEmailChanged?: (arg: UserEmailOrPhoneIdentifier) => void;
}

export default function UserEmailPhoneIdentifierSelector(props: Props) {
  const {
    defaultSelection,
    label,
    showPhoneLabelTips,
    onOptionChanged,
    onValueChanged,
    onPhoneChanged,
    onEmailChanged,
  } = props;

  const [selectedOption, setSelectedOption] = useState<SignInSignUpOption>(
    defaultSelection !== undefined ? defaultSelection : SignInSignUpOption.Email
  );

  const handleOptionChanged = (type: SignInSignUpOption) => {
    setSelectedOption(type);
    onOptionChanged?.(type);
  };

  const handlePhoneNumberChanged = (value: PhoneInputValueChangedEventArg) => {
    const data: UserEmailOrPhoneIdentifier = {
      type: SignInSignUpOption.Phone,
      phone: value.nationalNumber,
      phoneCode: value.countryCode,
      phoneNumberWithCode: value.number,
    };

    onPhoneChanged?.(data);
    onValueChanged?.(data);
  };

  const handleEmailChanged = ({ event }: any) => {
    const data: UserEmailOrPhoneIdentifier = {
      type: SignInSignUpOption.Email,
      email: event?.target?.value,
    };

    onEmailChanged?.(data);
    onValueChanged?.(data);
  };

  const OptionButton = ({
    type,
    text,
  }: {
    type: SignInSignUpOption;
    text: string;
  }) => {
    return (
      <Button
        onClick={() => handleOptionChanged(type)}
        variant={selectedOption === type ? "basicActive" : "basic"}
        text={text}
        className="w-1/2"
      />
    );
  };

  return (
    <>
      <div className="text-sm mb-2 font-medium">{label}</div>
      <div className="flex gap-x-3">
        <OptionButton type={SignInSignUpOption.Email} text="Email" />
        <OptionButton type={SignInSignUpOption.Phone} text="Phone" />
      </div>
      <div className="mt-4">
        {selectedOption === SignInSignUpOption.Email ? (
          <FormGroup label="Email">
            <InputField
              id="signUpEmail"
              mode="email"
              label="Email"
              maxLength={255}
              placeholder="e.g. john.doe@provider.com"
              name="email"
              required={true}
              defaultValue={props.email}
              onChange={handleEmailChanged}
            />
          </FormGroup>
        ) : (
          <FormGroup
            label="Phone (inc. country code)"
            tips={showPhoneLabelTips ? "Used to verify your login" : ""}
          >
            <PromineoPhoneInput
              onPhoneNumberChanged={handlePhoneNumberChanged}
              value={props.phoneNumberWithCode || ""}
              required
            />
          </FormGroup>
        )}
      </div>
    </>
  );
}
